import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../../../../store/utility';
import { backend_server_address } from '../../../../../backendServer';
import { getTree } from '../../../../../store/actions/treeActions/getTree';
import { isMaintenanceState } from '../../../../../store/actions/treeActions/statusSelectors';
import { analyticsAPI } from '../../overview/ducks';

const LIFECYCLE_URL = 'lifecycle/';
const LIFECYCLE_LATEST_URL = 'lifecycle/latest/';
const LIFECYCLE_SCHEMA_URL = 'lifecycle/schema/';
const LIFECYCLE_DISMISS_URL = 'lifecycle/dismiss_followup/';

export type LifecycleEventType =
    'operational' | 'planned_maintenance' | 'unplanned_maintenance' | 'not_operational' | 'damaged' | 'decommissioned';

export interface LifecycleAttachment {
    id: number;
    file: string;
    file_name: string;
    uploaded_at?: string;
    size?: number;
}

export interface AssetLifecycleEvent {
    id: number;
    asset: number;
    date: string;
    type: LifecycleEventType;
    title: string;
    description: string;
    maintenance_until: string | null;
    notifications_disabled: boolean;
    reporter: number | null;
    user: number | null;
    creation_date: string;
    attachments: LifecycleAttachment[];
}

/**
 * Where the asset sits in the maintenance cycle.
 * - `active`: inside the window — grey, editable end date and notifications.
 * - `pending_review`: the window expired and the backend auto-reverted it; the user must report what
 *   happened before the cycle closes.
 * - `none`: nothing to show.
 */
export type MaintenanceStage = 'none' | 'active' | 'pending_review';

/**
 * `latest` is filtered to answer exactly this: it answers with no event (→ null here) once the asset
 * is resolved, and only returns an `operational` event while its follow-up is still pending.
 *
 * The elapsed-window check covers the gap before the backend's auto-revert job catches up (it runs
 * every 5 minutes): until it does, the event still reads as a maintenance type even though the window
 * is over. Comparing against the client clock is only load-bearing inside that window.
 */
export const deriveMaintenanceStage = (event: AssetLifecycleEvent | null | undefined): MaintenanceStage => {
    if (!event) return 'none';
    // `latest` also answers for the lifecycle states this cycle doesn't cover (not operational,
    // damaged, decommissioned); only an `operational` one is the auto-reverted window awaiting input.
    if (!isMaintenanceState(event.type)) return event.type === 'operational' ? 'pending_review' : 'none';
    const elapsed = !!event.maintenance_until && new Date(event.maintenance_until) <= new Date();
    return elapsed ? 'pending_review' : 'active';
};

/**
 * The window the edit form opens on, or null to open a fresh one.
 *
 * `latest` also answers with the states this cycle doesn't cover (not operational, damaged,
 * decommissioned) and with the auto-reverted `operational` one. Handing any of those to the form
 * would put it in edit mode over a window that is not open, so ending it would move the asset to
 * operational with no maintenance to end.
 */
export const activeMaintenanceWindow = (event: AssetLifecycleEvent | null | undefined): AssetLifecycleEvent | null =>
    deriveMaintenanceStage(event) === 'active' ? (event ?? null) : null;

/**
 * The maintenance window a pending review is about. `latest` answers that review with the
 * auto-reverted `operational` event, which the revert job creates bare — no title, description,
 * notification switch or attachments — so the window's own data has to be read from the history.
 *
 * The list arrives newest-first (the endpoint orders by `-date`), so the first maintenance entry is
 * the window that just expired.
 */
export const findExpiredWindow = (events: AssetLifecycleEvent[] | undefined): AssetLifecycleEvent | null =>
    events?.find((event) => isMaintenanceState(event.type)) ?? null;

/** Attachments are re-sent on every edit: existing ones by id, new ones as files. */
export interface LifecycleAttachmentInput {
    keepIds?: number[];
    files?: File[];
}

/**
 * DRF's nested-list-over-multipart convention (`field[index]subfield`). Objects are JSON-encoded and
 * null/undefined dropped, since every multipart value reaches the backend as a string.
 */
const toMultipart = (fields: Record<string, unknown>, attachments: LifecycleAttachmentInput): FormData => {
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        form.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
    });
    let index = 0;
    (attachments.keepIds ?? []).forEach((id) => form.append(`attachments[${index++}]id`, String(id)));
    (attachments.files ?? []).forEach((file) => form.append(`attachments[${index++}]file`, file));
    return form;
};

/**
 * The POST answers with the tree rooted at the node, but that payload doesn't carry the lifecycle
 * fields the seeding reads, so the tree is refetched instead: `getTree` grafts the subtree into the
 * existing one and re-seeds the status slice, which is the path a page reload already exercises.
 */
const refreshAssetStateOnSuccess = async (
    { node }: { node: number },
    { dispatch, queryFulfilled }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<unknown> },
) => {
    try {
        await queryFulfilled;
        dispatch(getTree(node, null));
        dispatch(analyticsAPI.util.invalidateTags(['MachineHealthCount']));
    } catch {
        // The failure surfaces via the mutation's own error state.
    }
};

export const assetLifecycleAPI = createApi({
    reducerPath: 'assetLifecycleAPI',
    baseQuery: axiosBaseQuery({ baseUrl: backend_server_address + '/api/equipment_nodes/' }),
    tagTypes: ['LifecycleEvent', 'LifecycleSchema'],
    endpoints: (builder) => ({
        // "Resolved, nothing to show" is not a failure, so it resolves to null instead of lighting up
        // the caller's error state. The backend answers it with an empty 200 body, which axios hands
        // over as an empty string; older deployments answer it with a 404.
        getLatestLifecycleEvent: builder.query<AssetLifecycleEvent | null, number>({
            queryFn: async (node, _api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: LIFECYCLE_LATEST_URL,
                    method: 'GET',
                    withCredentials: true,
                    params: { node_id: node },
                });
                if ('error' in result) {
                    if ((result.error as { status?: number })?.status === 404) return { data: null };
                    return result as { error: unknown };
                }
                return { data: (result.data as AssetLifecycleEvent | '' | null) || null };
            },
            providesTags: (_r, _e, node) => [{ type: 'LifecycleEvent', id: node }],
        }),
        // The asset's lifecycle history, newest first. Read to recover the window behind a pending
        // review, which `latest` cannot report on its own.
        getLifecycleEvents: builder.query<AssetLifecycleEvent[], number>({
            query: (node) => ({ url: LIFECYCLE_URL, method: 'GET', withCredentials: true, params: { node } }),
            providesTags: (_r, _e, node) => [{ type: 'LifecycleEvent', id: node }],
        }),
        getLifecycleSchema: builder.query<Record<string, unknown>, void>({
            query: () => ({ url: LIFECYCLE_SCHEMA_URL, method: 'GET', withCredentials: true }),
            providesTags: ['LifecycleSchema'],
        }),
        // Enters, edits and ends a maintenance window: every POST is a new audit row, there is no PATCH,
        // so an edit resubmits the untouched fields too.
        setLifecycleEvent: builder.mutation<
            unknown,
            { node: number; data: Record<string, unknown>; attachments?: LifecycleAttachmentInput }
        >({
            query: ({ node, data, attachments }) => ({
                url: LIFECYCLE_URL,
                method: 'POST',
                withCredentials: true,
                data:
                    attachments?.files?.length || attachments?.keepIds?.length
                        ? toMultipart({ ...data, node }, attachments)
                        : { ...data, node },
            }),
            invalidatesTags: (_r, _e, { node }) => [{ type: 'LifecycleEvent', id: node }],
            onQueryStarted: refreshAssetStateOnSuccess,
        }),
        // Closes the pending-review prompt without recording an outcome. Mutates the pending event in
        // place, so no new lifecycle event (and no tree) comes back.
        dismissFollowup: builder.mutation<void, { node: number; comments?: string }>({
            query: ({ node, ...data }) => ({
                url: LIFECYCLE_DISMISS_URL,
                method: 'POST',
                withCredentials: true,
                data: { ...data, node },
            }),
            invalidatesTags: (_r, _e, { node }) => [{ type: 'LifecycleEvent', id: node }],
            onQueryStarted: refreshAssetStateOnSuccess,
        }),
    }),
});

export const {
    useGetLatestLifecycleEventQuery,
    useGetLifecycleEventsQuery,
    useGetLifecycleSchemaQuery,
    useSetLifecycleEventMutation,
    useDismissFollowupMutation,
} = assetLifecycleAPI;
export const assetLifecycleAPIReducer = assetLifecycleAPI.reducer;
export const assetLifecycleAPIReducerPath = assetLifecycleAPI.reducerPath;
export const assetLifecycleAPIReducerMiddleware = assetLifecycleAPI.middleware;
